import { Image } from 'expo-image';
import { useCallback, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { useCartValues } from '@fhs/cart/src/state/cart-store';
import {
  ActionSheet,
  Button,
  IconCheckFill,
  IconShoppingBag,
  RadioGroup,
  Text,
  tokens,
} from '@fhs/ui';

import { useAddGuideOfferToCart, useOfferCart } from '../../state/offer-guide-cart-state';

import { BottomButtonContainer } from './bottom-button';

function renderHeader() {
  return (
    <View style={styles.content}>
      <View style={styles.shoppingBagContainer}>
        <View>
          <IconShoppingBag size={40} color={tokens.colors.$success} />
          <IconCheckFill color={tokens.colors.$success} style={styles.checkIcon} />
        </View>
      </View>
      <Text.Heading type="one" style={styles.heading}>
        It seems like you have some items in your cart
      </Text.Heading>
      <Text.Ui size="md" style={styles.subheading}>
        Select an item from your cart to be redeemed or add a new one to the cart.
      </Text.Ui>
    </View>
  );
}

function renderSeparator() {
  return <View style={styles.separator} />;
}

export function AvailableItemsInCart() {
  const { steps: offerSteps, goFromScratch, applySelectedItem } = useOfferCart();
  const { addGuideOfferToCart } = useAddGuideOfferToCart();

  const { cartEntries } = useCartValues();
  const availableCartItemsFormatted = useMemo(() => {
    const firstStep = offerSteps[0];
    if (firstStep) {
      return firstStep.availableCartItems?.map(cartItem => {
        const cartEntry = cartEntries.find(entry => entry.cartId === cartItem);
        const eligibleItemImage = firstStep.eligibleItems.find(
          eligibleItem => eligibleItem.id === cartEntry?._id
        )?.image?.asset?.uri;

        return {
          image: eligibleItemImage ?? '',
          name: cartEntry?.name ?? '',
          onPress: () => setSelectedAvailableItem(cartItem),
          value: cartItem,
        };
      });
    }
    return [];
  }, [cartEntries, offerSteps]);

  const [selectedAvailableItem, setSelectedAvailableItem] = useState<string>(() =>
    availableCartItemsFormatted && availableCartItemsFormatted[0]?.value
      ? availableCartItemsFormatted[0]?.value
      : ''
  );

  const addNewItem = useCallback(() => {
    goFromScratch();
  }, [goFromScratch]);

  const redeemSelectedItem = useCallback(() => {
    const cartEntry = cartEntries.find(entry => entry.cartId === selectedAvailableItem);
    if (cartEntry) {
      applySelectedItem(cartEntry);
      addGuideOfferToCart();
    }
  }, [applySelectedItem, addGuideOfferToCart, cartEntries, selectedAvailableItem]);

  const renderItem = useCallback(
    ({
      item: { image, name, onPress, value },
    }: {
      item: {
        image: string;
        name: string;
        onPress: () => void;
        value: string;
      };
    }) => {
      return (
        <Pressable style={styles.itemContainer} onPress={onPress}>
          <Image contentFit="contain" source={{ uri: image }} style={styles.itemImage} />
          <View style={styles.itemContent}>
            <Text.Ui numberOfLines={1} size="md">
              {name}
            </Text.Ui>
          </View>
          <RadioGroup value={selectedAvailableItem} onChange={onPress}>
            <RadioGroup.Item value={value} label="Other">
              <RadioGroup.CircleIndicator />
            </RadioGroup.Item>
          </RadioGroup>
        </Pressable>
      );
    },
    [selectedAvailableItem]
  );

  return (
    <View style={styles.container}>
      <ActionSheet.FlatList
        bounces={false}
        showsVerticalScrollIndicator={false}
        data={availableCartItemsFormatted}
        ListHeaderComponent={renderHeader}
        renderItem={renderItem}
        ItemSeparatorComponent={renderSeparator}
        ListFooterComponent={renderSeparator}
      />
      <BottomButtonContainer style={styles.buttonContainer}>
        <Button size="xl" onPress={redeemSelectedItem} disabled={!selectedAvailableItem}>
          <Button.Text>Redeem Selected Item</Button.Text>
        </Button>
        <Button type="outline" size="xl" onPress={addNewItem}>
          <Button.Text>Add New Item</Button.Text>
        </Button>
      </BottomButtonContainer>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingBottom: 24,
    paddingHorizontal: 16,
    alignItems: 'center',
    gap: 16,
  },
  shoppingBagContainer: {
    backgroundColor: tokens.colors.$blackOpacity04,
    borderRadius: 200,
    width: 64,
    height: 64,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkIcon: {
    position: 'absolute',
    top: -7,
    right: -3,
  },
  heading: {
    textAlign: 'center',
  },
  subheading: {
    textAlign: 'center',
  },
  itemContainer: {
    padding: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    borderRadius: 8,
    marginHorizontal: 16,
  },
  itemImage: {
    width: 40,
    height: 40,
  },
  itemContent: {
    flex: 1,
  },
  separator: {
    marginBottom: 12,
  },
  buttonContainer: {
    gap: 12,
  },
});
